import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import { MenuProps } from '@mui/material/Menu';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Box,
  Typography,
  Tooltip,
  Drawer,
  Container,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import { Table, TableBody, TableCell, TableHead, TableRow, TablePagination, tableCellClasses } from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetZoomIcon,
PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { GridColDef, GridRowParams, GridRenderCellParams } from '@mui/x-data-grid';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchExpensePayments,
  deleteExpensePayment,
  exportExpenseReport,
  fetchExpenseCategories,
  fetchExpenseVendors,
  approveExpensePayment,
  rejectExpensePayment
} from '../../store/expenseStore/expenseThunk';
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from '../../utils/api.types';
import { ExpenseFilters, ExpensePayment } from '../../utils/expense.types';
import { Utils } from '../../utils/common-utils';
import CustomDataGrid from '../DataGrid/DataGrid';
import ExpenseFilter from './ExpenseFilter';
import CustomSelect from '../FormFields/CustomSelect';
import DialogAddExpenseForm from './DialogAddExpenseForm';
import DialogSettleExpense from './DialogSettleExpense';
import DialogDocAttachForm from '../DocumentAttachment/DialogDocAttachForm';

import styled_ from "@emotion/styled";
import Menu from '@mui/material/Menu';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': { padding: '4px 0' },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': { fontSize: 18, color: theme.palette.text.secondary, marginRight: theme.spacing(1.5) },
      '&:active': { backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity) },
    },
  },
}));

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#e9ecf1',
    color: '#545454',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: '6px 12px',
    fontSize: 14,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0,
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: '6px 12px',
    border: '0.75px solid #d9d4d4',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  backgroundColor: '#fff',
  '&:last-child td, &:last-child th': {
  },
}));



interface ExpenseListProps {
  accountType?: string;
}

const ExpenseList: FC<ExpenseListProps> = (props) => {
  const dispatch = useAppDispatch();
  const { payments, rowCount, categories, categoryDropdownItems, vendors, paymentsStatus, status, errorMessage } = useAppSelector(state => state.expense);
  const { selectedAccount, user } = useAppSelector(state => state.auth);

  // Check if logged-in user is admin/manager/owner
  const isAdmin = selectedAccount?.owner?._id === user?._id || selectedAccount?.owner === user?._id || user?.role?.some((r: any) => ['admin', 'Admin', 'ADMIN', 'manager', 'Manager', 'MANAGER', 'Owner', 'owner', 'OWNER'].includes(r.role));

  // Check if logged-in user is an employee (non-admin)
  const isEmployeeUser = !isAdmin && user?.isEmployee && user?.employeeId;
  const loggedInEmployeeId = user?.employeeId;

  const [filters, setFilters] = useState<ExpenseFilters>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(apiLimit.limit);
  const [openExpenseDrawer, setOpenExpenseDrawer] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpensePayment | null>(null);
  const [isRefresh, setIsRefresh] = useState('');
  const [openSettleDialog, setOpenSettleDialog] = useState(false);
  const [expenseToSettle, setExpenseToSettle] = useState<ExpensePayment | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuExpense, setActionMenuExpense] = useState<ExpensePayment | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ open: boolean; attachments: any[]; currentIndex: number }>({ open: false, attachments: [], currentIndex: 0 });
  const [imgZoom, setImgZoom] = useState(100);
  const [imgPan, setImgPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [editExpense, setEditExpense] = useState<ExpensePayment | null>(null);

  // Confirmation dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveReason, setApproveReason] = useState('');
  const [dialogExpense, setDialogExpense] = useState<ExpensePayment | null>(null);

  // Memoized callback for filter changes - reset to first page
  const handleFiltersChange = useCallback((newFilters: ExpenseFilters) => {
    setPage(0);
    setFilters(newFilters);
  }, []);

  // Load initial data on component mount
  useEffect(() => {
    if (selectedAccount) {
      dispatch(fetchExpenseCategories({ flat: false }));
      dispatch(fetchExpenseVendors({ accountId: selectedAccount._id }));
    }
  }, [dispatch, selectedAccount]);

  // Load expenses when filters, page, pageSize or refresh changes
  useEffect(() => {
    if (selectedAccount && filters) {
      const finalFilters = { ...filters, page, limit: pageSize };
      // For employee users, always filter by their employee ID
      if (isEmployeeUser && loggedInEmployeeId) {
        finalFilters.employee = loggedInEmployeeId;
      }
      dispatch(fetchExpensePayments(finalFilters));
    }
  }, [dispatch, selectedAccount, filters, page, pageSize, isRefresh, isEmployeeUser, loggedInEmployeeId]);

  useEffect(() => {
    if (page > 0 && rowCount > 0 && page * pageSize >= rowCount) {
      setPage(Math.max(0, Math.ceil(rowCount / pageSize) - 1));
    }
  }, [page, pageSize, rowCount]);

  const handleRefresh = () => {
    setIsRefresh(Date.now().toString());
  };

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visiblePayments = useMemo(() => {
    const allPayments = payments || [];
    const isClientSidePaginationNeeded =
      allPayments.length > pageSize || (rowCount > 0 && allPayments.length === rowCount);

    if (!isClientSidePaginationNeeded) {
      return allPayments;
    }

    const startIndex = page * pageSize;
    return allPayments.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, payments, rowCount]);

  const handleOpenExpense = () => { setEditExpense(null); setOpenExpenseDrawer(true); };

  const handleEditExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setEditExpense(expense);
    setOpenExpenseDrawer(true);
  };

  const handleCloseExpense = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenExpenseDrawer(false);
    setEditExpense(null);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await dispatch(deleteExpensePayment(expenseId)).unwrap();
      handleRefresh();
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  const handleExport = async (format: 'excel' | 'pdf' = 'excel') => {
    try {
      await dispatch(exportExpenseReport({ format, filters })).unwrap();
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, expense: ExpensePayment) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuExpense(expense);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuExpense(null);
  };

  const handleSettleExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setExpenseToSettle(expense);
    setOpenSettleDialog(true);
  };

  const handleCloseSettleDialog = (settled?: boolean) => {
    setOpenSettleDialog(false);
    setExpenseToSettle(null);
    if (settled) {
      handleRefresh();
    }
  };

  // Open confirmation dialogs
  const handleDeleteFromMenu = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setDeleteDialogOpen(true);
  };

  const handleApproveExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setApproveDialogOpen(true);
  };

  const handleRejectExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  // Confirm actions
  const confirmDelete = async () => {
    if (dialogExpense) {
      await handleDeleteExpense(dialogExpense._id);
    }
    setDeleteDialogOpen(false);
    setDialogExpense(null);
  };

  const confirmApprove = async () => {
    if (dialogExpense) {
      try {
        await dispatch(approveExpensePayment({ expenseId: dialogExpense._id, remarks: approveReason || undefined })).unwrap();
        handleRefresh();
      } catch (error) {
        console.error('Failed to approve expense:', error);
      }
    }
    setApproveDialogOpen(false);
    setDialogExpense(null);
    setApproveReason('');
  };

  const confirmReject = async () => {
    if (dialogExpense) {
      try {
        await dispatch(rejectExpensePayment({ expenseId: dialogExpense._id, reason: rejectReason || undefined })).unwrap();
        handleRefresh();
      } catch (error) {
        console.error('Failed to reject expense:', error);
      }
    }
    setRejectDialogOpen(false);
    setDialogExpense(null);
    setRejectReason('');
  };

  const getPaymentModeColor = (mode: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' } = {
      'CASH': 'success',
      'CHEQUE': 'primary',
      'CARD': 'secondary',
      'UPI': 'info',
      'NETBANKING': 'warning',
    };
    return colors[mode] || 'default';
  };

  const getStatusChipStyle = (status: string) => {
    const styles: { [key: string]: { bg: string; color: string; label: string } } = {
      'BOOKED': { bg: '#fff3e0', color: '#e65100', label: 'Booked' },
      'APPROVED': { bg: '#e3f2fd', color: '#1565c0', label: 'Approved' },
      'PAID': { bg: '#e8f5e9', color: '#2e7d32', label: 'Paid' },
      'REJECTED': { bg: '#ffebee', color: '#c62828', label: 'Rejected' },
      'EXPENSE_RECORDED': { bg: '#f3e5f5', color: '#7b1fa2', label: 'Recorded' },
    };
    return styles[status] || { bg: '#f5f5f5', color: '#616161', label: status };
  };

  const getCategoryName = (categoryCode: string) => {
    const category = categories?.find(cat => cat.code === categoryCode);
    return category?.name || categoryCode;
  };

  /**
   * Column definitions for expense payments data grid.
   */
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "paymentDate",
      headerName: 'Date',
      type: "string",
      flex: 1.2,
      renderCell: ({ row }: GridRenderCellParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
          <span style={{ fontWeight: '500', fontSize: '9pt' }}>{moment(row.paymentDate).format("DD MMM YYYY")}</span>
          <span style={{ fontSize: '7.5pt', color: '#6b7280' }}>{moment(row.paymentDate).format("hh:mm A")}</span>
        </div>
      )
    },
    {
      field: "paymentTo",
      headerName: 'Party & Reference',
      type: "string",
      flex: 2.5,
      renderCell: ({ row }: GridRenderCellParams) => {
        const isAcct = row.paymentFor === 'account';
        const name = isAcct
          ? (selectedAccount?.legalName || selectedAccount?.name || 'Self Account')
          : row.employee?.name || row.paymentTo?.legalName || row.paymentTo?.name || '-';
        const refId = row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
            <span style={{ fontWeight: '500', fontSize: '9pt' }}>{name}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              {isAcct ? (
                <Chip label="Account" size="small" style={{ fontSize: '7pt', height: '16px', backgroundColor: '#e0f2f1', color: '#00695c', width: 'fit-content' }} />
              ) : row.paymentFor === 'employee' ? (
                <Chip label="Employee" size="small" style={{ fontSize: '7pt', height: '16px', backgroundColor: '#e3f2fd', color: '#1565c0', width: 'fit-content' }} />
              ) : (
                <Chip label="Vendor" size="small" style={{ fontSize: '7pt', height: '16px', backgroundColor: '#edf6ef', color: '#49a563', width: 'fit-content' }} />
              )}
              {refId && (
                <Chip label={refId} size="small" style={{ fontSize: '7pt', height: '16px', backgroundColor: '#f5f3ff', color: '#6366f1', fontWeight: 400, width: 'fit-content' }} />
              )}
            </div>
          </div>
        );
      }
    },
    {
      field: "expenseCategory",
      headerName: 'Category',
      type: "string",
      flex: 1.5,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Typography variant="body2" fontSize="9pt">{row?.expenseCategory?.name || ''}</Typography>
      )
    },
    {
      field: "paymentMode",
      headerName: 'Mode',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        <span style={{ fontStyle: 'italic', fontSize: '9pt' }}>{row.paymentMode ? Utils.capitalizeFirstLetter(row.paymentMode.toLowerCase()) : '-'}</span>
      )
    },
    {
      field: "amount",
      headerName: 'Amount',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>{row.amount > 0 ? Utils.formatNumber(parseFloat(row.amount).toFixed(2)) : ''}</Typography>
      )
    },
    {
      field: "paymentStatus",
      headerName: 'Status',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const s = getStatusChipStyle(row.paymentStatus);
        return <Chip label={s.label} size="small" style={{ fontSize: '7.5pt', height: '20px', fontWeight: 600, backgroundColor: s.bg, color: s.color, borderRadius: '4px' }} />;
      }
    },
    {
      field: "attachments",
      headerName: 'Slip',
      type: "string",
      flex: 0.8,
      renderCell: ({ row }: GridRenderCellParams) => (
        row.attachments && row.attachments.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => setAttachmentPreview({ open: true, attachments: row.attachments, currentIndex: 0 })}>
            {row.attachments[0].mimeType === 'application/pdf' || row.attachments[0].originalname?.toLowerCase().endsWith('.pdf') ? (
              <PictureAsPdfIcon sx={{ fontSize: 30, color: '#ef4444' }} />
            ) : (
              <img style={{ maxWidth: '40px', maxHeight: '40px', borderRadius: 4, border: '1px solid #e0e0e0' }} src={row.attachments[0].url} alt="" />
            )}
            {row.attachments.length > 1 && (
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>+{row.attachments.length - 1}</Typography>
            )}
          </Box>
        ) : <Typography variant="caption" color="textSecondary">-</Typography>
      )
    },
    {
      field: "remarks",
      headerName: 'Remarks',
      type: "string",
      flex: 1.5,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Tooltip title={row.remarks || ''}>
          <Typography variant="body2" fontSize="9pt">{row.remarks || '-'}</Typography>
        </Tooltip>
      )
    },
    {
      field: "action",
      headerName: 'Action',
      type: "string",
      flex: 0.8,
      renderCell: ({ row }: GridRenderCellParams) => (
        <IconButton size="small" onClick={(e) => handleOpenActionMenu(e, row as ExpensePayment)}>
          <MoreVertIcon />
        </IconButton>
      )
    }
  ], [categories, isAdmin]);

  const handleRowClick = (params: GridRowParams) => {
    setSelectedExpense(params.row as ExpensePayment);
  };

  const isPaymentsLoading = status === ApiStatus.LOADING;

  return (
    <Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ mt: 0, mb: 1, fontWeight: 600, fontSize: '1.5rem', color: '#111827' }}>
            Expense Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Record and track vendor payments and miscellaneous expenses
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('excel')}
            variant="outlined"
            size="small"
            sx={{ color: '#059669', borderColor: '#059669', '&:hover': { borderColor: '#047857', backgroundColor: '#ecfdf5' }, textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
            sx={{ color: '#059669', borderColor: '#059669', '&:hover': { borderColor: '#047857', backgroundColor: '#ecfdf5' }, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenExpense}
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' }, textTransform: 'none' }}
          >
            Record Expense
          </Button>
        </Box>
      </Box>

      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1" sx={{ mt: 2, mb: 1 }}>
            Expense Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Record and track vendor payments and miscellaneous expenses
          </Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('excel')}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenExpense}
            variant="contained"
            size="small"
          >
            Record Expense
          </Button>
        </Box>
      </Box> */}


      <Grid container spacing={2} >
        <Grid item xs={12}>
          <Box sx={{ width: '100%' }} className={'c-card2'}>
            <Box p={2} style={{ borderBottom: '.5px solid #9da7b7' }}>
              <ExpenseFilter
                onFiltersChange={handleFiltersChange}
                currentFilters={filters}
                categories={categories || []}
                hideVendorSearch={!!isEmployeeUser}
              />
            </Box>
            {rowCount > 0 ? (
              <>
                <Table stickyHeader sx={{ minWidth: 700 }} size='small'>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell>Party & Reference</StyledTableCell>
                      <StyledTableCell style={{ width: 130, maxWidth: 130 }}>Category</StyledTableCell>
                      <StyledTableCell>Payment</StyledTableCell>
                      <StyledTableCell style={{ width: 200, maxWidth: 200 }}>Remarks</StyledTableCell>
                      <StyledTableCell style={{ width: 180, maxWidth: 180 }}>Approval</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody key={`${page}-${pageSize}`}>
                    {visiblePayments
                      .map((row: any, index: number) => (
                      <StyledTableRow key={row._id || index}>
                        <StyledTableCell>
                          {(row.paymentDate && row.paymentDate != '') ? (
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                                <span style={{ fontWeight: '500', fontSize: '9pt', color: '#111827' }}>{moment(row.paymentDate).format("DD MMM YYYY")}</span>
                                <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '8pt', marginTop: '2px' }}>{moment(row.paymentDate).format("hh:mm A")}</span>

                            </div>
                          ) : '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                            <span style={{ fontWeight: '500' }}>{
                              row.paymentFor === 'account'
                                ? (selectedAccount?.legalName || selectedAccount?.name || 'Self Account')
                                : row.employee?.name || row.paymentTo?.legalName || '-'
                            }</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                              {row.paymentFor === 'account' ? (
                                <Chip label="Account" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e0f2f1', color: '#00695c', width: 'fit-content' }} />
                              ) : row.paymentFor === 'employee' ? (
                                <Chip label="Employee" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e3f2fd', color: '#1565c0', width: 'fit-content' }} />
                              ) : (
                                <Chip label="Vendor" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#edf6ef', color: '#49a563', width: 'fit-content' }} />
                              )}
                              <Chip
                                label={row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '')}
                                size="small"
                                style={{ fontSize: 11, height: '16px', backgroundColor: '#f5f3ff', color: '#6366f1', fontWeight: 400, width: 'fit-content' }}
                              />
                            </div>
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 130, maxWidth: 130 }}>{row?.expenseCategory?.name || ''}</StyledTableCell>
                        <StyledTableCell>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.3' }}>
                            <span style={{ fontWeight: 600 }}>{row.amount > 0 ? Utils.formatNumber(parseFloat(row.amount).toFixed(2)) : ''}</span>
                            <span style={{ fontStyle: 'italic', fontSize: '8pt', color: '#6b7280' }}>{row.paymentMode ? `via ${Utils.capitalizeFirstLetter(row.paymentMode.toLowerCase())}` : ''}</span>
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 220, maxWidth: 220 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {row?.remarks && (
                              <div>
                                <Tooltip title={`By ${row?.createdBy?.name || ''}${row?.createdAt ? ' · ' + moment(row.createdAt).format('DD MMM, hh:mm A') : ''}`} placement="top-start" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#1f2937', color: '#f9fafb', fontSize: '0.75rem', fontWeight: 500, px: 1.5, py: 0.75, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' } }, arrow: { sx: { color: '#1f2937' } } }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, overflow: 'hidden' }}>
                                    <span style={{ fontSize: '7pt', fontWeight: 600, color: '#fff', backgroundColor: (row?.paymentStatus === 'PAID' && !row?.settlementRemarks) ? '#2e7d32' : '#e65100', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>{(row?.paymentStatus === 'PAID' && !row?.settlementRemarks) ? 'Paid' : 'Booked'}</span>
                                    <span style={{ fontSize: '7pt', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                      {row?.createdBy?.name ? `By ${row.createdBy.name}` : ''}
                                      {row?.createdAt ? ` · ${moment(row.createdAt).format('DD MMM, hh:mm A')}` : ''}
                                    </span>
                                  </div>
                                </Tooltip>
                                <span style={{ fontSize: '8.5pt', color: '#374151', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {row.remarks}
                                </span>
                              </div>
                            )}
                            {row?.settlementRemarks && (
                              <div style={{ borderTop: row?.remarks ? '1px dashed #e5e7eb' : 'none', paddingTop: row?.remarks ? 3 : 0 }}>
                                <Tooltip title={`By ${row?.updatedBy?.name || ''}${row?.updatedAt ? ' · ' + moment(row.updatedAt).format('DD MMM, hh:mm A') : ''}`} placement="top-start" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#1f2937', color: '#f9fafb', fontSize: '0.75rem', fontWeight: 500, px: 1.5, py: 0.75, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' } }, arrow: { sx: { color: '#1f2937' } } }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, overflow: 'hidden' }}>
                                    <span style={{ fontSize: '7pt', fontWeight: 600, color: '#fff', backgroundColor: '#2e7d32', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>Paid</span>
                                    <span style={{ fontSize: '7pt', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                      {row?.updatedBy?.name ? `By ${row.updatedBy.name}` : ''}
                                      {row?.updatedAt ? ` · ${moment(row.updatedAt).format('DD MMM, hh:mm A')}` : ''}
                                    </span>
                                  </div>
                                </Tooltip>
                                <span style={{ fontSize: '8.5pt', color: '#374151', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {row.settlementRemarks}
                                </span>
                              </div>
                            )}
                            {!row?.remarks && !row?.settlementRemarks && (
                              <Typography variant="caption" color="textSecondary">-</Typography>
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 180, maxWidth: 180 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {(row?.approvedRemarks || (((row?.paymentStatus === 'APPROVED') || (row?.paymentStatus === 'PAID')) && row?.approvedBy)) ? (
                              <div>
                                <Tooltip title={`By ${row?.approvedBy?.name || ''}${row?.approvedAt ? ' · ' + moment(row.approvedAt).format('DD MMM, hh:mm A') : ''}`} placement="top-start" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#1f2937', color: '#f9fafb', fontSize: '0.75rem', fontWeight: 500, px: 1.5, py: 0.75, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' } }, arrow: { sx: { color: '#1f2937' } } }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, overflow: 'hidden' }}>
                                    <span style={{ fontSize: '7pt', fontWeight: 600, color: '#fff', backgroundColor: '#1565c0', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>Approved</span>
                                    <span style={{ fontSize: '7pt', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                      {row?.approvedBy?.name ? `By ${row.approvedBy.name}` : ''}
                                      {row?.approvedAt ? ` · ${moment(row.approvedAt).format('DD MMM, hh:mm A')}` : ''}
                                    </span>
                                  </div>
                                </Tooltip>
                                {row?.approvedRemarks && (
                                  <span style={{ fontSize: '8.5pt', color: '#374151', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {row.approvedRemarks}
                                  </span>
                                )}
                              </div>
                            ) : null}
                            {(row?.rejectedRemarks || ((row?.paymentStatus === 'REJECTED') && row?.rejectedBy)) ? (
                              <div style={{ borderTop: (row?.approvedRemarks || row?.approvedBy) ? '1px dashed #e5e7eb' : 'none', paddingTop: (row?.approvedRemarks || row?.approvedBy) ? 3 : 0 }}>
                                <Tooltip title={`By ${row?.rejectedBy?.name || ''}${row?.rejectedAt ? ' · ' + moment(row.rejectedAt).format('DD MMM, hh:mm A') : ''}`} placement="top-start" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#1f2937', color: '#f9fafb', fontSize: '0.75rem', fontWeight: 500, px: 1.5, py: 0.75, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' } }, arrow: { sx: { color: '#1f2937' } } }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, overflow: 'hidden' }}>
                                    <span style={{ fontSize: '7pt', fontWeight: 600, color: '#fff', backgroundColor: '#c62828', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>Rejected</span>
                                    <span style={{ fontSize: '7pt', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                      {row?.rejectedBy?.name ? `By ${row.rejectedBy.name}` : ''}
                                      {row?.rejectedAt ? ` · ${moment(row.rejectedAt).format('DD MMM, hh:mm A')}` : ''}
                                    </span>
                                  </div>
                                </Tooltip>
                                {row?.rejectedRemarks && (
                                  <span style={{ fontSize: '8.5pt', color: '#374151', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {row.rejectedRemarks}
                                  </span>
                                )}
                              </div>
                            ) : null}
                            {!row?.approvedBy && !row?.approvedRemarks && !row?.rejectedBy && !row?.rejectedRemarks && (
                              <Typography variant="caption" color="textSecondary">-</Typography>
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell>
                          {(() => { const s = getStatusChipStyle(row?.paymentStatus); return <Chip label={s.label} size="small" style={{ fontSize: 13, height: '24px', fontWeight: 600, backgroundColor: s.bg, color: s.color, borderRadius: '4px' }} />; })()}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isAdmin && row.paymentStatus === 'BOOKED' && (
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => handleApproveExpense(row)} style={{ color: '#16a34a', backgroundColor: '#f0fdf4', border: '0.1px solid #16a34a', height: 30, width: 30 }}>
                                  <ApproveIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isAdmin && row.paymentStatus === 'BOOKED' && (
                              <Tooltip title="Reject">
                                <IconButton size="small" onClick={() => handleRejectExpense(row)} style={{ color: '#dc2626', backgroundColor: '#fef2f2', border: '0.1px solid #dc2626', height: 30, width: 30 }}>
                                  <RejectIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {row.paymentStatus === 'REJECTED' && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditExpense(row)} style={{ color: '#488dff', backgroundColor: '#fff', border: '0.1px solid #488dff', height: 30, width: 30 }}>
                                  <EditIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isAdmin && row.paymentStatus === 'APPROVED' && (
                              <Tooltip title="Pay Now">
                                <IconButton size="small" onClick={() => handleSettleExpense(row)} style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 30, width: 30 }}>
                                  <PaymentIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDeleteFromMenu(row)} style={{ color: '#dc2626', backgroundColor: '#fef2f2', border: '0.1px solid #dc2626', height: 30, width: 30 }}>
                                <DeleteIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            {row.attachments && row.attachments.length > 0 && (
                              <Tooltip title={`${row.attachments.length} Attachment${row.attachments.length > 1 ? 's' : ''}`}>
                                <IconButton size="small" onClick={() => setAttachmentPreview({ open: true, attachments: row.attachments, currentIndex: 0 })} style={{ color: '#7c8db0', backgroundColor: '#f1f3f7', border: '0.1px solid #7c8db0', height: 30, width: 30 }}>
                                  <DescriptionIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={rowCount}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={defaultRowsPerPageOptions}
                  showFirstButton
                  showLastButton
                />
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptIcon sx={{ fontSize: 50, color: '#bdbdbd', mb: 1 }} />
                <Typography variant="h6" color="textSecondary">No Expenses Found</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenExpense} size="small" sx={{ mt: 2 }}>Record Expense</Button>
              </Box>
            )}
          </Box>

        </Grid>
      </Grid>

      {/* Expense Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openExpenseDrawer}
      >
        <DialogAddExpenseForm
          title={editExpense ? "Edit Expense" : "Record Expense Payment"}
          open={openExpenseDrawer}
          onClose={handleCloseExpense}
          editExpense={editExpense}
          isEmployeeUser={!!isEmployeeUser}
          loggedInEmployeeId={loggedInEmployeeId}
          loggedInEmployeeName={user?.name}
        />
      </Drawer>

      {/* Action Menu - Same as Sales Listing */}
      <StyledMenu
        id="expense-action-menu"
        MenuListProps={{ 'aria-labelledby': 'expense-action-button' }}
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'BOOKED' && (
          <MenuItem onClick={() => handleApproveExpense(actionMenuExpense)} disableRipple>
            <ApproveIcon style={{ color: '#16a34a', border: '1.5px solid #16a34a', borderRadius: '50%', backgroundColor: '#fff', padding: 1 }} />
            Approve
          </MenuItem>
        )}
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'BOOKED' && (
          <MenuItem onClick={() => handleRejectExpense(actionMenuExpense)} disableRipple>
            <RejectIcon style={{ color: '#dc2626', border: '1.5px solid #dc2626', borderRadius: '50%', backgroundColor: '#fff', padding: 1 }} />
            Reject
          </MenuItem>
        )}
        {actionMenuExpense && actionMenuExpense.paymentStatus === 'REJECTED' && (
          <MenuItem onClick={() => handleEditExpense(actionMenuExpense)} disableRipple>
            <EditIcon color='primary' />
            Edit Expense
          </MenuItem>
        )}
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'APPROVED' && (
          <MenuItem onClick={() => handleSettleExpense(actionMenuExpense)} disableRipple>
            <PaymentIcon color='success' />
            Pay Now
          </MenuItem>
        )}
        {actionMenuExpense && (
          <MenuItem onClick={() => handleDeleteFromMenu(actionMenuExpense)} disableRipple>
            <DeleteIcon color='error' />
            Delete
          </MenuItem>
        )}
      </StyledMenu>

      {/* Settle Expense Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openSettleDialog}
      >
        <DialogSettleExpense
          open={openSettleDialog}
          expense={expenseToSettle}
          onClose={handleCloseSettleDialog}
        />
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDialogExpense(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#fee2e2', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Delete Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>This action cannot be undone</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box sx={{ mt:1,mb: 1, display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>Expense ID</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>
                {dialogExpense?.referenceId || (dialogExpense?.txnId ? `EXP-${String(dialogExpense.txnId).padStart(3, '0')}` : '-')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>Against</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                {dialogExpense?.paymentFor === 'account'
                  ? (selectedAccount?.legalName || selectedAccount?.name || 'Self Account')
                  : dialogExpense?.employee?.name || dialogExpense?.paymentTo?.legalName || '-'}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" sx={{ color: '#1f2937' }}>
            Are you sure you want to delete this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          {dialogExpense?.paymentStatus === 'PAID' && (
            <Typography variant="body2" sx={{ color: '#b45309', mt: 1.5, backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 1.5, py: 1 }}>
              This is a paid expense. All associated journal entries and ledger postings will also be reversed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setDeleteDialogOpen(false); setDialogExpense(null); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => { setApproveDialogOpen(false); setDialogExpense(null); setApproveReason(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#f0fdf4', borderBottom: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#dcfce7', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ApproveIcon sx={{ color: '#16a34a', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Approve Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>Approve this expense for payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1" sx={{ color: '#1f2937', pt: 1, mb: 2 }}>
            Are you sure you want to approve this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          <TextField
            label="Remarks (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setApproveDialogOpen(false); setDialogExpense(null); setApproveReason(''); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmApprove} variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => { setRejectDialogOpen(false); setDialogExpense(null); setRejectReason(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#fee2e2', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RejectIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Reject Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>Provide a reason for rejection</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1" sx={{ color: '#1f2937', pt: 1, mb: 2 }}>
            Are you sure you want to reject this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          <TextField
            label="Reason for rejection (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setRejectDialogOpen(false); setDialogExpense(null); setRejectReason(''); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmReject} variant="contained" startIcon={<RejectIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Attachment Preview Dialog with Gallery */}
      <Dialog
        open={attachmentPreview.open}
        onClose={() => { setAttachmentPreview({ open: false, attachments: [], currentIndex: 0 }); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
        onKeyDown={(e) => {
          if (attachmentPreview.attachments.length <= 1) return;
          if (e.key === 'ArrowRight' && attachmentPreview.currentIndex < attachmentPreview.attachments.length - 1) {
            setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
            setImgZoom(100); setImgPan({ x: 0, y: 0 });
          } else if (e.key === 'ArrowLeft' && attachmentPreview.currentIndex > 0) {
            setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 }));
            setImgZoom(100); setImgPan({ x: 0, y: 0 });
          }
        }}
      >
        {attachmentPreview.attachments.length > 0 && (() => {
          const att = attachmentPreview.attachments[attachmentPreview.currentIndex];
          const attName = att?.originalname || att?.attachmentLabel || 'Slip';
          const attUrl = att?.url || '';
          const isPdf = att?.mimeType === 'application/pdf' || attName.toLowerCase().endsWith('.pdf');
          return (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {attName}
                  {attachmentPreview.attachments.length > 1 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: '#666' }}>
                      ({attachmentPreview.currentIndex + 1} / {attachmentPreview.attachments.length})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isPdf && (
                    <>
                      <IconButton size="small" onClick={() => setImgZoom(z => Math.max(z - 25, 25))}><ZoomOutIcon sx={{ fontSize: 20 }} /></IconButton>
                      <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'center', fontWeight: 600 }}>{imgZoom}%</Typography>
                      <IconButton size="small" onClick={() => setImgZoom(z => Math.min(z + 25, 300))}><ZoomInIcon sx={{ fontSize: 20 }} /></IconButton>
                      <IconButton size="small" onClick={() => { setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}><ResetZoomIcon sx={{ fontSize: 18 }} /></IconButton>
                    </>
                  )}
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} href={attUrl} target="_blank" download sx={{ textTransform: 'none', ml: 1 }}>Download</Button>
                  <IconButton size="small" onClick={() => { setAttachmentPreview({ open: false, attachments: [], currentIndex: 0 }); setImgZoom(100); }}><CloseIcon /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 2, textAlign: 'center', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isPdf ? (
                  <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
                    <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={32} />
                      <Typography variant="caption" color="textSecondary">Loading PDF...</Typography>
                    </Box>
                    <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(attUrl)}&embedded=true`} width="100%" height="100%" title="PDF Preview" style={{ border: 'none', position: 'relative', zIndex: 1, backgroundColor: 'white' }} />
                  </Box>
                ) : (
                  <Box
                    sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: imgZoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in', userSelect: 'none' }}
                    onWheel={(e) => {
                      e.preventDefault();
                      const delta = e.deltaY > 0 ? -10 : 10;
                      setImgZoom(z => {
                        const newZoom = Math.max(50, Math.min(z + delta, 400));
                        if (newZoom <= 100) { setImgPan({ x: 0, y: 0 }); }
                        return newZoom;
                      });
                    }}
                    onMouseDown={(e) => {
                      if (imgZoom > 100) {
                        setIsDragging(true);
                        setDragStart({ x: e.clientX - imgPan.x, y: e.clientY - imgPan.y });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (isDragging && imgZoom > 100) {
                        setImgPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
                      }
                    }}
                    onMouseUp={() => setIsDragging(false)}
                    onMouseLeave={() => setIsDragging(false)}
                    onDoubleClick={() => { setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}
                  >
                    <img
                      src={attUrl}
                      alt={attName}
                      draggable={false}
                      style={{
                        maxWidth: imgZoom === 100 ? '100%' : 'none',
                        maxHeight: imgZoom === 100 ? '100%' : 'none',
                        width: imgZoom !== 100 ? `${imgZoom}%` : undefined,
                        objectFit: 'contain',
                        transform: `translate(${imgPan.x}px, ${imgPan.y}px)`,
                        transition: isDragging ? 'none' : 'transform 0.1s ease',
                      }}
                    />
                  </Box>
                )}
                {/* Prev / Next buttons */}
                {attachmentPreview.attachments.length > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button size="small" variant="outlined" disabled={attachmentPreview.currentIndex === 0} onClick={() => { setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 })); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}>Prev</Button>
                    <Button size="small" variant="outlined" disabled={attachmentPreview.currentIndex === attachmentPreview.attachments.length - 1} onClick={() => { setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 })); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}>Next</Button>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default ExpenseList;
